import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { vitrineCardApi, type OnboardingSnapshot } from '@/api/vitrineCard';

/**
 * Последняя известная стадия, пережившая перезагрузку.
 *
 * Нужна ровно для первого кадра. Нижняя панель рисуется раньше, чем приходит снапшот, и без подсказки
 * набор вкладок в первом кадре заведомо неверен: показать все — они схлопнутся, показать минимум — у
 * клиента с картой они потом появятся. Любой из вариантов виден глазом (08.09.2026). Подсказка снимает
 * оба случая: возвращающийся клиент получает правильную панель сразу.
 *
 * Хранится только стадия — короткая строка вроде `AWAIT_TOPUP`, без сумм, имён и идентификаторов. Это
 * подсказка для отрисовки, а НЕ источник прав: доступ решает серверный снапшот и маршрутный гейт.
 * Чистится при выходе вместе со снапшотом.
 */
const STAGE_HINT_KEY = 'vitrine-stage-hint';

function readStageHint(): OnboardingSnapshot['stage'] | null {
  try { return (localStorage.getItem(STAGE_HINT_KEY) as OnboardingSnapshot['stage']) || null; }
  catch { return null; }   // private mode — просто останемся без подсказки
}

/**
 * Cached view of the server-owned onboarding snapshot (docs/ONBOARDING_BUG_REVIEW_2026-08-15.md §7.1).
 *
 * Kept in a store rather than fetched per-view because two consumers need it: the screens (what to render) and
 * the router guard (whether a card-only route may be entered at all). One fetch, one truth — a view and a guard
 * disagreeing about the stage is exactly the split-brain this design removes.
 */
export const useOnboardingStore = defineStore('onboarding', () => {
  const snapshot = ref<OnboardingSnapshot | null>(null);
  const loading = ref(false);
  /** Подсказка для первого кадра; живёт, пока снапшот не пришёл. */
  const stageHint = ref<OnboardingSnapshot['stage'] | null>(readStageHint());
  /** In-flight request, so concurrent callers (guard + view) share one round-trip instead of racing. */
  let inFlight: Promise<OnboardingSnapshot | null> | null = null;

  // ВАЖНО: `stage` остаётся строго снапшотным. Его читает маршрутный гейт, а тот не имеет права запирать
  // клиента по устаревшей подсказке — «неизвестно» для него означает «пропустить», и это должно сохраниться.
  const stage = computed(() => snapshot.value?.stage ?? null);
  /**
   * Стадия ДЛЯ ОТРИСОВКИ: снапшот, а пока его нет — подсказка с прошлого запуска.
   *
   * Отдельно от `stage` намеренно. Здесь ошибка стоит дёшево (на кадр раньше показали правильную панель),
   * а в гейте — дорого (заперли живого клиента), поэтому смешивать источники в одном поле нельзя.
   */
  const displayStage = computed(() => snapshot.value?.stage ?? stageHint.value ?? null);
  const nextAction = computed(() => snapshot.value?.nextAction ?? null);
  /** Card-only surfaces (history, transfers, generic top-up) unlock only once the card exists. */
  const cardUnlocked = computed(() => snapshot.value?.stage === 'ISSUED');

  async function load(force = false): Promise<OnboardingSnapshot | null> {
    if (!force && snapshot.value) return snapshot.value;
    // A forced refresh must NOT piggyback on a request that was issued before the change it is meant to see —
    // that is how an accepted offer kept rendering the offer hero.
    if (inFlight && !force) return inFlight;
    loading.value = true;
    inFlight = vitrineCardApi.onboarding()
      .then(s => { snapshot.value = s; rememberStage(s?.stage ?? null); return s; })
      // A failed probe must not lock the client out of the app: leave the snapshot null and let the caller
      // fall back (the guard treats "unknown" as permissive, the screens show their own error state).
      .catch(() => null)
      .finally(() => { loading.value = false; inFlight = null; });
    return inFlight;
  }

  /** Запомнить стадию для первого кадра следующего запуска. */
  function rememberStage(s: OnboardingSnapshot['stage'] | null) {
    stageHint.value = s;
    try {
      if (s) localStorage.setItem(STAGE_HINT_KEY, s);
      else localStorage.removeItem(STAGE_HINT_KEY);
    } catch { /* private mode — переживём без подсказки */ }
  }

  function reset() {
    snapshot.value = null;
    inFlight = null;
    // Выход — это и конец подсказки: следующий вход может быть другим человеком, и рисовать ему панель
    // по чужой стадии нельзя.
    rememberStage(null);
  }

  return { snapshot, loading, stage, displayStage, nextAction, cardUnlocked, load, reset };
});
